import { defineReactiveModel, Persistable, belongsTo } from "vue-app-utils";
import Model from "@/models/model";
import { createSession } from "@/models/session";
import { ordinal, rating } from "openskill";
import { ordinalToScaledDisplayRating } from "@/utils/scaled-display-rating";
import { getTotalMatches } from "@/utils/match-count";
import { pointsEarnedInMatch } from "@/utils/tournament-points";

const Player = {
  extends: Model,
  mixins: [
    Persistable,
    belongsTo("tournament")
  ],
  data () {
    return {
      playerUserId: "",
      userModel: null,
      present: false,
      startOfTournamentMu: 0,
      startOfTournamentSigma: 0,
      numMissedMatches: 0
    };
  },
  created () {
    let resolveUserModel;
    this.promiseUserModel = new Promise((resolve) => {
      resolveUserModel = resolve;
    });
    this.$watch("playerUserId", (id) => {
      if (id) {
        createSession(this.$store).findUserById(id).then((user) => {
          this.userModel = user;
          resolveUserModel(user);
        });
      }
    }, { immediate: true });
  },
  computed: {
    name () {
      return this.userModel?.playerName || "";
    },
    tournamentOrdinal () {
      return ordinal(rating(this.tournament.playerRatings[this.id]));
    },
    tournamentDisplayRating () {
      return ordinalToScaledDisplayRating(this.tournamentOrdinal);
    },
    wins () {
      return this.tournament.inactiveMatches.filter(m => {
        const playerTeam = m.teams.find(t => t.playerIds.includes(this.id));
        if (!playerTeam) return false;
        const maxGoals = Math.max(...m.teams.map(t => t.goals));
        return playerTeam.goals === maxGoals;
      }).length;
    },
    losses () {
      return this.tournament.inactiveMatches.filter(m => {
        const playerTeam = m.teams.find(t => t.playerIds.includes(this.id));
        if (!playerTeam) return false;
        const maxGoals = Math.max(...m.teams.map(t => t.goals));
        return playerTeam.goals < maxGoals;
      }).length;
    },
    nearLosses () {
      return this.tournament.inactiveMatches.filter(m => {
        if (m.teams.length !== 2) return false;
        const playerTeam = m.teams.find(t => t.playerIds.includes(this.id));
        if (!playerTeam) return false;
        const maxGoals = Math.max(...m.teams.map(t => t.goals));
        return maxGoals - playerTeam.goals === 1;
      }).length;
    },
    goalDiff () {
      return this.tournament.inactiveMatches.reduce((diff, m) => {
        const playerTeam = m.teams.find(t => t.playerIds.includes(this.id));
        if (!playerTeam) return diff;
        return diff + playerTeam.goals - (m.teams.reduce((sum, t) => sum + t.goals, 0) - playerTeam.goals);
      }, 0);
    },
    goalsFor () {
      return this.tournament.inactiveMatches.reduce((sum, m) => {
        const playerTeam = m.teams.find(t => t.playerIds.includes(this.id));
        return playerTeam ? sum + playerTeam.goals : sum;
      }, 0);
    },
    goalsAgainst () {
      return this.tournament.inactiveMatches.reduce((sum, m) => {
        const playerTeam = m.teams.find(t => t.playerIds.includes(this.id));
        if (!playerTeam) return sum;
        return sum + m.teams.reduce((total, t) => total + t.goals, 0) - playerTeam.goals;
      }, 0);
    },
    tournamentPoints () {
      return this.tournament.inactiveMatches.reduce((total, m) => {
        return total + pointsEarnedInMatch(m, this.id);
      }, 0);
    },
    totalMatches () {
      return getTotalMatches(this);
    },
    singlesCount () {
      return this.tournament.inactiveMatches.filter(m => {
        return m.teams.some(t => t.playerIds.includes(this.id) && t.playerIds.length === 1);
      }).length;
    },
    active () {
      return this.tournament.activeMatches.some(m => {
        return m.teams.some(t => t.playerIds.includes(this.id));
      });
    }
  },
  methods: {
    async awaitUserModelAndScorecard () {
      await this.promiseUserModel;
      this.userModel.scorecard;
      await this.userModel.promiseScorecard;
    }
  }
};

export default defineReactiveModel(Player);
